import React, { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronRight, Download, Link2Off } from 'lucide-react'
import type { BuildRange, ClinSparkStudyPackage } from '../../types'
import { anchorId, downloadText, known, pluralize, rows, scrollToElement, sourceShortLabel, toCsv, type Row } from '../../utils/parser'
import { Badge, Highlight, Metric, Notice, SearchInput, Section, SegmentedControl } from './StudyUi'
import type { FocusRequest, NavigateFn } from './BuildBlueprint'

type TypeFilter = 'all' | 'eligibility' | 'monitoring'
type StatusFilter = 'all' | 'mapped' | 'unmapped' | 'review'

const CATEGORY_ORDER = ['Demographics', 'Anthropometrics', 'Vital Signs', 'ECG', 'Laboratory', 'Cognitive / Scales']

export function packageRanges(data: ClinSparkStudyPackage): BuildRange[] {
  const cmm = (data.study_build_design?.CMM || {}) as Row
  // The CMM copy carries procedure/form/visit mapping; the package copy is a fallback.
  const source = Array.isArray(cmm.range_dictionary) ? cmm.range_dictionary : data.ranges
  return rows(source).filter((item) => item.stated && typeof item.stated === 'object') as unknown as BuildRange[]
}

function rangeText(range: BuildRange) {
  return [range.item_name, range.category, range.criterion_ref, range.population, range.qualifier, range.stated?.display,
    range.acceptable?.display, range.source_text, range.source?.text_snippet, ...(range.procedure_names || []), ...(range.form_names || [])]
    .filter(Boolean).join(' ').toLowerCase()
}

export default function RangesTab({ data, search, onSearchChange, onNavigate, focus }: {
  data: ClinSparkStudyPackage
  search: string
  onSearchChange: (next: string) => void
  onNavigate?: NavigateFn
  focus?: FocusRequest | null
}) {
  const ranges = useMemo(() => packageRanges(data), [data])
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [category, setCategory] = useState('all')
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  const [flash, setFlash] = useState<string | null>(null)

  const categories = useMemo(() => {
    const present = [...new Set(ranges.map((range) => range.category || 'Other'))]
    return present.sort((a, b) => (CATEGORY_ORDER.indexOf(a) + 1 || 99) - (CATEGORY_ORDER.indexOf(b) + 1 || 99))
  }, [ranges])
  const multiDocument = useMemo(() => new Set(ranges.flatMap((range) => (range.source_documents || []).map((ref) => ref.protocol_id || ref.filename))).size > 1, [ranges])

  useEffect(() => {
    if (!focus) return
    setTypeFilter('all')
    setStatusFilter('all')
    setCategory('all')
    const rangeId = focus.anchor.replace(/^range-/, '')
    setOpen((current) => new Set(current).add(rangeId))
    const id = window.setTimeout(() => {
      scrollToElement(document.getElementById(focus.anchor), 'center')
      setFlash(focus.anchor)
    }, 0)
    return () => window.clearTimeout(id)
  }, [focus])
  useEffect(() => {
    if (!flash) return
    const id = window.setTimeout(() => setFlash(null), 1800)
    return () => window.clearTimeout(id)
  }, [flash])

  const query = search.trim().toLowerCase()
  const visible = ranges.filter((range) =>
    (typeFilter === 'all' || (typeFilter === 'eligibility' ? range.range_type === 'eligibility' : range.range_type !== 'eligibility'))
    && (category === 'all' || (range.category || 'Other') === category)
    && (statusFilter === 'all'
      || (statusFilter === 'mapped' && range.mapping_status === 'MAPPED')
      || (statusFilter === 'unmapped' && range.mapping_status !== 'MAPPED')
      || (statusFilter === 'review' && range.human_review_required))
    && (!query || rangeText(range).includes(query)))

  const counts = {
    eligibility: ranges.filter((range) => range.range_type === 'eligibility').length,
    monitoring: ranges.filter((range) => range.range_type !== 'eligibility').length,
    mapped: ranges.filter((range) => range.mapping_status === 'MAPPED').length,
    review: ranges.filter((range) => range.human_review_required).length,
  }

  const exportCsv = () => {
    const header = ['Range ID', 'Parameter', 'Category', 'Type', 'Qualifier', 'Population', 'Acceptable range', 'Protocol states', 'Unit', 'Source', 'PDF page', 'Procedures', 'Forms', 'Visits', 'Mapping', 'Needs review', 'Review reasons']
    const body = ranges.map((range) => [
      range.range_id, range.item_name, range.category, range.range_type === 'eligibility' ? 'Eligibility' : 'Monitoring',
      [range.qualifier, range.statistic, range.relative_to_baseline ? 'change from baseline' : ''].filter(Boolean).join(', '),
      range.population || '', range.acceptable?.display || '', range.stated?.display || '', range.unit || '',
      range.criterion_ref || range.source?.section || '', range.source?.page ?? '',
      (range.procedure_names || []).join('; '), (range.form_names || []).join('; '), (range.visit_names || []).join('; '),
      range.mapping_status, range.human_review_required ? 'Yes' : 'No', (range.review_reasons || []).join('; '),
    ])
    downloadText('protocol_ranges.csv', toCsv(header, body), 'text/csv;charset=utf-8')
  }

  if (!ranges.length) {
    return (
      <Section title="Range Definitions" description="Numeric limits stated by the protocol, mapped to the procedures and forms that collect them.">
        <Notice>No explicit numeric ranges or thresholds were found in the eligibility criteria or safety sections. Ranges are only taken from protocol wording; none are inferred from clinical convention.</Notice>
      </Section>
    )
  }

  const toggle = (rangeId: string) => setOpen((current) => {
    const next = new Set(current)
    if (next.has(rangeId)) next.delete(rangeId)
    else next.add(rangeId)
    return next
  })

  return (
    <Section
      title="Range Definitions"
      description="Numeric limits stated by the protocol. Eligibility thresholds are converted to the value window a participant must fall in (an exclusion of “SBP > 150 mmHg” becomes “≤ 150 mmHg”); monitoring thresholds are shown as stated."
      actions={<button type="button" className="btn-secondary" onClick={exportCsv}><Download className="mr-1.5 h-4 w-4" aria-hidden={true} />Export CSV</button>}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Metric label="Ranges" value={ranges.length} />
        <Metric label="Eligibility" value={counts.eligibility} />
        <Metric label="Monitoring / safety" value={counts.monitoring} />
        <Metric label="Mapped to procedures" value={`${counts.mapped} / ${ranges.length}`} tone={counts.mapped === ranges.length ? 'green' : undefined} />
        <Metric label="Needs review" value={counts.review} tone={counts.review ? 'amber' : undefined} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={search} onChange={onSearchChange} placeholder="Search ranges, procedures, criteria" className="w-full sm:w-72" />
        <SegmentedControl label="Range type" options={[['all', 'All'], ['eligibility', 'Eligibility'], ['monitoring', 'Monitoring']] as const} valueKey={typeFilter} onChange={setTypeFilter} />
        <SegmentedControl label="Mapping status" options={[['all', 'Any status'], ['mapped', 'Mapped'], ['unmapped', 'Unmapped'], ['review', 'Needs review']] as const} valueKey={statusFilter} onChange={setStatusFilter} />
        {categories.length > 1 && (
          <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Category" className="rounded-lg border border-gray-300 bg-white py-1.5 pl-2 pr-8 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
            <option value="all">All categories</option>
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        )}
        <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">{visible.length === ranges.length ? pluralize(ranges.length, 'range') : `${visible.length} of ${ranges.length} ranges`}</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full min-w-[960px]">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="w-8 px-2" aria-label="Details" />
              {['Parameter', 'Build range', 'Protocol states', 'Source', 'Mapped to', 'Status'].map((column) => (
                <th key={column} className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">{column}</th>
              ))}
            </tr>
          </thead>
          {categories.filter((item) => category === 'all' || item === category).map((group) => {
            const groupRows = visible.filter((range) => (range.category || 'Other') === group)
            if (!groupRows.length) return null
            return (
              <tbody key={group} className="divide-y divide-gray-100 dark:divide-gray-700/70">
                <tr className="bg-gray-50/70 dark:bg-gray-800/50">
                  <th colSpan={7} scope="colgroup" className="px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">{group} <span className="font-normal text-gray-400">{groupRows.length}</span></th>
                </tr>
                {groupRows.map((range) => (
                  <RangeRow key={range.range_id} range={range} expanded={open.has(range.range_id)} onToggle={() => toggle(range.range_id)} flash={flash === anchorId('range', range.range_id)} search={search} multiDocument={multiDocument} onNavigate={onNavigate} />
                ))}
              </tbody>
            )
          })}
        </table>
        {visible.length === 0 && <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No ranges match the current filters.</p>}
      </div>
    </Section>
  )
}

function RangeRow({ range, expanded, onToggle, flash, search, multiDocument, onNavigate }: {
  range: BuildRange
  expanded: boolean
  onToggle: () => void
  flash: boolean
  search: string
  multiDocument: boolean
  onNavigate?: NavigateFn
}) {
  const eligibility = range.range_type === 'eligibility'
  const build = eligibility ? range.acceptable : range.stated
  const qualifiers = [
    range.qualifier ? (range.qualifier === 'male' ? 'Males' : 'Females') : null,
    range.statistic ? `${range.statistic[0].toUpperCase()}${range.statistic.slice(1)}` : null,
    range.relative_to_baseline ? 'Change from baseline' : null,
  ].filter((item): item is string => Boolean(item))
  const procedures = range.procedure_names || []
  const criterionMatch = String(range.criterion_ref || '').match(/^(INC|EXC)\s*#\s*(.+)$/i)
  const detailsId = `${anchorId('range', range.range_id)}-details`
  return (
    <>
      <tr id={anchorId('range', range.range_id)} className={`scroll-mt-24 align-top text-sm transition-colors ${flash ? 'bg-primary-50 dark:bg-primary-950/40' : 'hover:bg-gray-50/70 dark:hover:bg-gray-800/40'}`}>
        <td className="px-2 py-3">
          <button type="button" onClick={onToggle} aria-expanded={expanded} aria-controls={detailsId} aria-label={`${expanded ? 'Hide' : 'Show'} details for ${range.item_name}`} className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200">
            <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} aria-hidden={true} />
          </button>
        </td>
        <td className="px-3 py-3">
          <div className="font-medium text-gray-900 dark:text-white"><Highlight text={range.item_name} query={search} /></div>
          <div className="mt-1 flex flex-wrap gap-1">
            {qualifiers.map((item) => <Badge key={item} tone="violet">{item}</Badge>)}
            {range.population && <Badge>{range.population}</Badge>}
          </div>
        </td>
        <td className="px-3 py-3">
          <div className="whitespace-nowrap font-mono text-sm font-semibold text-gray-900 dark:text-white">{build?.display || '-'}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{eligibility ? 'Acceptable values' : 'Alert threshold'}</div>
        </td>
        <td className="max-w-xs px-3 py-3">
          <div className="text-gray-700 dark:text-gray-200">{range.stated?.display}</div>
          {range.source_text && <div className="truncate text-xs italic text-gray-500 dark:text-gray-400" title={range.source?.text_snippet || range.source_text}>“{range.source_text}”</div>}
        </td>
        <td className="px-3 py-3 text-xs">
          {criterionMatch && onNavigate ? (
            <button type="button" onClick={() => onNavigate('ie', anchorId('ie', `${criterionMatch[1].toUpperCase()}#${criterionMatch[2].trim().toLowerCase()}`))} className="font-semibold text-primary-700 hover:underline dark:text-primary-300" title="Open criterion">{range.criterion_ref}</button>
          ) : (
            <span className="font-medium text-gray-700 dark:text-gray-200">{range.criterion_ref || range.source?.section || '-'}</span>
          )}
          {known(range.source?.page) && <div className="text-gray-500 dark:text-gray-400">PDF p. {String(range.source?.page)}</div>}
          {multiDocument && (range.source_documents || []).map((ref) => <div key={ref.protocol_id || ref.filename} className="text-gray-500 dark:text-gray-400" title={ref.filename}>{sourceShortLabel(ref)}</div>)}
        </td>
        <td className="max-w-[16rem] px-3 py-3 text-xs text-gray-700 dark:text-gray-200">
          {procedures.length ? (
            <>
              <div className="truncate" title={procedures.join('\n')}>{procedures.slice(0, 2).join(', ')}{procedures.length > 2 ? ` +${procedures.length - 2}` : ''}</div>
              {(range.visit_ids || []).length > 0 && <div className="text-gray-500 dark:text-gray-400">{pluralize(range.visit_ids.length, 'visit')}</div>}
            </>
          ) : <span className="text-gray-400">Not mapped</span>}
        </td>
        <td className="px-3 py-3">
          <div className="flex flex-col items-start gap-1">
            {range.mapping_status === 'MAPPED'
              ? <Badge tone="green"><CheckCircle2 className="h-3 w-3" aria-hidden={true} />Mapped</Badge>
              : <Badge tone="amber"><Link2Off className="h-3 w-3" aria-hidden={true} />Unmapped</Badge>}
            {range.human_review_required && <Badge tone="amber" title={(range.review_reasons || []).join('\n')}><AlertTriangle className="h-3 w-3" aria-hidden={true} />Review</Badge>}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr id={detailsId} className="bg-gray-50/60 dark:bg-gray-900/40">
          <td />
          <td colSpan={6} className="px-3 pb-4 pt-1 text-sm">
            <dl className="grid gap-x-6 gap-y-3 md:grid-cols-2">
              <Detail label="Protocol text">{range.source?.text_snippet || range.source_text || '-'}</Detail>
              <Detail label="Mapping">{range.mapping_reason || '-'}</Detail>
              {(range.review_reasons || []).length > 0 && (
                <Detail label="Why review is needed">
                  <ul className="list-disc space-y-0.5 pl-4">{range.review_reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
                </Detail>
              )}
              <Detail label="Procedures">{procedures.join(', ') || '-'}</Detail>
              <Detail label="Forms">{(range.form_names || []).join(', ') || '-'}</Detail>
              <Detail label="Visits">{(range.visit_names || []).join(', ') || '-'}</Detail>
              {(range.lab_panel_names || []).length > 0 && <Detail label="Laboratory panels">{range.lab_panel_names.join(', ')}</Detail>}
              <Detail label="Range ID">{range.range_id} · confidence {Math.round((range.confidence || 0) * 100)}%</Detail>
            </dl>
          </td>
        </tr>
      )}
    </>
  )
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-gray-800 dark:text-gray-200">{children}</dd>
    </div>
  )
}
